import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Activity, RefreshCw, Loader2, Server, HardDrive, TrendingUp,
  AlertTriangle, Download, Save, CheckCircle, Clock, ChevronDown, ChevronUp,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { previewPacingFromTarget, estimateActualRatePerHour } from '../lib/postfixPacing';
import type {
  PostfixOverview,
  PostfixSnapshot,
  PostfixEvent,
  PostfixError,
  PostfixDomainRow,
  PostfixMonitorSettings,
  PostfixHealth,
  PostfixHistoricalSummary,
} from '../types';

/** Chart + historical KPI lookback (independent of log tables). */
const CHART_RANGE_OPTIONS = [
  { value: '1h', label: 'Past hour' },
  { value: '24h', label: 'Past day' },
  { value: '7d', label: 'Past week' },
  { value: '30d', label: 'Past month' },
  { value: '90d', label: 'Past 3 months' },
] as const;

/** Deliveries / errors / domains tables (event retention is usually shorter). */
const LOGS_RANGE_OPTIONS = [
  { value: '1h', label: 'Last hour' },
  { value: '6h', label: 'Last 6 hours' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
] as const;

const DOMAIN_PREVIEW_COUNT = 5;

type ChartRange = typeof CHART_RANGE_OPTIONS[number]['value'];
type LogsRange = typeof LOGS_RANGE_OPTIONS[number]['value'];

function healthBadgeClass(h: PostfixHealth): string {
  if (h === 'green') return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
  if (h === 'yellow') return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
  if (h === 'red') return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  return 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300';
}

function formatRate(n: number): string {
  if (!n || n <= 0) return 'No cap';
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k/hr`;
  return `${n.toLocaleString()}/hr`;
}

function formatLiveRate(n: number): string {
  if (!n || n <= 0) return '0/hr';
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k/hr`;
  return `${n.toLocaleString()}/hr`;
}

function formatQueueDrain(queue: number, rate5mPerHour: number): { value: string; sub: string } {
  if (queue <= 0) return { value: 'Empty', sub: 'Nothing waiting in Postfix' };
  if (!rate5mPerHour || rate5mPerHour <= 0) {
    return { value: '—', sub: 'No send rate in last 5 min' };
  }
  const hours = queue / rate5mPerHour;
  const totalMin = Math.max(1, Math.round(hours * 60));
  if (totalMin < 60) {
    return { value: `${totalMin} min`, sub: 'Queue ÷ now (5 min) rate' };
  }
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  const value = m === 0 ? `${h}h` : `${h}h ${m}m`;
  return { value, sub: 'Queue ÷ now (5 min) rate' };
}

function KpiCard({ icon, label, value, sub }: {
  icon: React.ReactNode; label: string; value: React.ReactNode; sub?: React.ReactNode;
}) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-4">
      <div className="flex items-center gap-2 text-gray-500 dark:text-slate-400 text-xs font-medium uppercase tracking-wide">
        {icon}{label}
      </div>
      <div className="text-xl font-bold text-gray-900 dark:text-white mt-2">{value}</div>
      {sub && <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

function chartTimeLabel(iso: string, range: ChartRange): string {
  const d = new Date(iso);
  if (range === '1h' || range === '24h') {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (range === '7d') {
    return d.toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export default function MetricsLogs() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [chartRange, setChartRange] = useState<ChartRange>('7d');
  const [logsRange, setLogsRange] = useState<LogsRange>('24h');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overview, setOverview] = useState<PostfixOverview | null>(null);
  const [historical, setHistorical] = useState<PostfixHistoricalSummary | null>(null);
  const [snapshots, setSnapshots] = useState<PostfixSnapshot[]>([]);
  const [events, setEvents] = useState<PostfixEvent[]>([]);
  const [errors, setErrors] = useState<PostfixError[]>([]);
  const [domains, setDomains] = useState<PostfixDomainRow[]>([]);
  const [showAllDomains, setShowAllDomains] = useState(false);
  const [errorFilter, setErrorFilter] = useState('serious');
  const [settings, setSettings] = useState<PostfixMonitorSettings | null>(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const settingsDirtyRef = useRef(false);
  const loadGenRef = useRef(0);

  const load = useCallback(async () => {
    const gen = ++loadGenRef.current;
    setLoading(true);
    setError(null);
    try {
      const [ov, hist, snap, ev, err, dom, sett] = await Promise.all([
        axios.get<PostfixOverview>('/api/metrics/postfix/overview'),
        axios.get<PostfixHistoricalSummary>(`/api/metrics/postfix/historical-summary?range=${chartRange}`),
        axios.get<PostfixSnapshot[]>(`/api/metrics/postfix/snapshots?range=${chartRange}`),
        axios.get<PostfixEvent[]>(`/api/metrics/postfix/events?range=${logsRange}&status=sent`),
        axios.get<PostfixError[]>(`/api/metrics/postfix/errors?range=${logsRange}&category=${errorFilter}`),
        axios.get<PostfixDomainRow[]>(`/api/metrics/postfix/domain-breakdown?range=${logsRange}`),
        axios.get<PostfixMonitorSettings>('/api/metrics/postfix/settings'),
      ]);
      if (gen !== loadGenRef.current) return;
      setOverview(ov.data);
      setHistorical(hist.data);
      setSnapshots(snap.data);
      setEvents(ev.data);
      setErrors(err.data);
      setDomains(dom.data);
      if (!settingsDirtyRef.current) {
        setSettings(sett.data);
      }
    } catch (e: any) {
      if (gen !== loadGenRef.current) return;
      setError(e?.response?.data?.error ?? e?.message ?? 'Failed to load metrics');
    } finally {
      if (gen === loadGenRef.current) setLoading(false);
    }
  }, [chartRange, logsRange, errorFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const id = setInterval(load, (settings?.refresh_seconds ?? 60) * 1000);
    return () => clearInterval(id);
  }, [load, settings?.refresh_seconds]);

  const downloadCsv = async (type: string) => {
    try {
      const range = type === 'snapshots' ? chartRange : logsRange;
      const res = await axios.get(`/api/metrics/postfix/export?type=${type}&range=${range}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `postfix-${type}-${range}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (e: any) {
      if (e?.response?.status !== 401 && e?.response?.status !== 403) {
        window.alert('CSV export failed');
      }
    }
  };

  const saveSettings = async () => {
    if (!settings || !isAdmin) return;
    setSettingsSaving(true);
    setSettingsSaved(false);
    try {
      const payload = {
        queue_warning_count: settings.queue_warning_count,
        queue_critical_count: settings.queue_critical_count,
        disk_warning_gb: settings.disk_warning_gb,
        disk_critical_gb: settings.disk_critical_gb,
        refresh_seconds: settings.refresh_seconds,
        retention_snapshots_days: settings.retention_snapshots_days,
        retention_events_days: settings.retention_events_days,
        retention_errors_days: settings.retention_errors_days,
        serious_error_threshold: settings.serious_error_threshold,
        target_hourly_rate: settings.target_hourly_rate ?? 0,
        smtp_destination_concurrency_limit: settings.smtp_destination_concurrency_limit ?? 1,
        smtp_destination_rate_delay_ms: settings.smtp_destination_rate_delay_ms ?? 0,
        smtp_destination_recipient_limit: settings.smtp_destination_recipient_limit ?? 50,
      };
      const res = await axios.put<PostfixMonitorSettings>('/api/metrics/postfix/settings', payload);
      loadGenRef.current += 1;
      settingsDirtyRef.current = false;
      setSettings({
        ...settings,
        ...payload,
        ...res.data,
      });
      setSettingsSaved(true);
      if (res.data.smtp_pacing_applied === false && res.data.smtp_pacing_message) {
        window.alert(res.data.smtp_pacing_message);
      }
      setTimeout(() => setSettingsSaved(false), 3000);
    } catch (e: any) {
      const data = e?.response?.data;
      const extra = Array.isArray(data?.details) ? `\n${data.details.join('\n')}` : '';
      window.alert((data?.error ?? 'Failed to save settings') + extra);
    } finally {
      setSettingsSaving(false);
    }
  };

  const targetRate = settings?.target_hourly_rate ?? 0;
  const manualConc = settings?.smtp_destination_concurrency_limit ?? 1;
  const manualDelaySec = Math.round((settings?.smtp_destination_rate_delay_ms ?? 0) / 1000);
  const pacingPreview = previewPacingFromTarget(targetRate);
  const manualEst = estimateActualRatePerHour(manualConc, manualDelaySec);

  const applyTargetPreset = (target: number) => {
    if (!settings) return;
    const preview = previewPacingFromTarget(target);
    settingsDirtyRef.current = true;
    setSettings({
      ...settings,
      target_hourly_rate: target,
      smtp_destination_concurrency_limit: preview.concurrency,
      smtp_destination_rate_delay_ms: preview.delaySec * 1000,
    });
  };

  const setManualPacing = (conc: number, delaySec: number) => {
    if (!settings) return;
    const c = Math.min(20, Math.max(1, Math.round(conc) || 1));
    const d = Math.min(99, Math.max(0, Math.round(delaySec) || 0));
    settingsDirtyRef.current = true;
    setSettings({
      ...settings,
      smtp_destination_concurrency_limit: c,
      smtp_destination_rate_delay_ms: d * 1000,
      target_hourly_rate: estimateActualRatePerHour(c, d),
    });
  };

  const chartData = snapshots.map((s) => ({
    time: chartTimeLabel(s.captured_at, chartRange),
    rate: s.rate_60m_per_hour,
    queue: s.postfix_queue_count,
    errors: s.serious_errors_15m,
    disk: s.disk_free_gb,
  }));

  const chartRangeLabel =
    CHART_RANGE_OPTIONS.find((o) => o.value === chartRange)?.label ?? chartRange;
  const queueDrain = overview
    ? formatQueueDrain(overview.postfix_queue_count, overview.rate_5m_per_hour)
    : null;

  if (loading && !overview) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-400 gap-2">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span>Loading metrics…</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Activity className="h-7 w-7 text-indigo-500" />
            Metrics &amp; Logs
          </h1>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
            Postfix / SMTP.com delivery monitoring
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg text-sm text-red-700 dark:text-red-400">
          <AlertTriangle className="h-4 w-4" />
          {error}
        </div>
      )}

      {overview && (
        <>
          {/* A: Live Overview — current ops only */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Live Overview</h2>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${healthBadgeClass(overview.health)}`}>
                {overview.health}
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-slate-400 mb-4">{overview.health_reason}</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
              <KpiCard icon={<Server className="h-4 w-4" />} label="Postfix" value={overview.postfix_status} />
              <KpiCard
                icon={<Activity className="h-4 w-4" />}
                label="Queue"
                value={overview.postfix_queue_count.toLocaleString()}
                sub={`${overview.postfix_queue_kbytes.toLocaleString()} KB`}
              />
              <KpiCard
                icon={<TrendingUp className="h-4 w-4" />}
                label="Now (5 min)"
                value={formatLiveRate(overview.rate_5m_per_hour)}
                sub={`${overview.sent_5m.toLocaleString()} sent last 5 min`}
              />
              {queueDrain && (
                <KpiCard
                  icon={<Clock className="h-4 w-4" />}
                  label="Est. Delivery Time"
                  value={queueDrain.value}
                  sub={queueDrain.sub}
                />
              )}
              <KpiCard
                icon={<TrendingUp className="h-4 w-4" />}
                label="Last 60 min"
                value={formatLiveRate(overview.rate_60m_per_hour)}
                sub={`${overview.sent_60m.toLocaleString()} sent in last 60 min`}
              />
              <KpiCard
                icon={<AlertTriangle className="h-4 w-4" />}
                label="Errors"
                value={overview.serious_errors_15m}
                sub="Serious, last 15 min"
              />
              <KpiCard
                icon={<HardDrive className="h-4 w-4" />}
                label="Disk Free"
                value={overview.disk_free_gb != null ? `${overview.disk_free_gb} GB` : '—'}
                sub={overview.disk_used_percent != null ? `${overview.disk_used_percent}% used` : undefined}
              />
              <KpiCard
                icon={<Activity className="h-4 w-4" />}
                label="Target Rate"
                value={formatRate(overview.target_hourly_rate)}
                sub={overview.target_hourly_rate > 0 ? 'Configured target' : 'No cap'}
              />
              <KpiCard icon={<Activity className="h-4 w-4" />} label="Active Jobs" value={overview.active_jobs_count} />
              <KpiCard
                icon={<RefreshCw className="h-4 w-4" />}
                label="Last Snapshot"
                value={overview.last_updated ? new Date(overview.last_updated).toLocaleString() : '—'}
              />
            </div>
          </section>

          {/* B: Active Jobs */}
          <section className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white px-4 py-3 border-b border-gray-100 dark:border-slate-700">
              Active / Paused Interspire Jobs
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-slate-700/60">
                  <tr>
                    {['Job ID', 'Status', 'Queue ID', 'Newsletter', 'Job Time', 'Total', 'Processed', 'Unprocessed', '%', 'ETA'].map((h) => (
                      <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                  {(overview.active_jobs ?? []).length === 0 ? (
                    <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-400">No active or paused jobs</td></tr>
                  ) : (overview.active_jobs ?? []).map((j) => (
                    <tr key={j.jobid} className="hover:bg-gray-50 dark:hover:bg-slate-700/40">
                      <td className="px-3 py-2">{j.jobid}</td>
                      <td className="px-3 py-2">{j.st === 'i' ? 'In Progress' : j.st === 'p' ? 'Paused' : j.st}</td>
                      <td className="px-3 py-2">{j.queueid}</td>
                      <td className="px-3 py-2">{j.newsletterid}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{j.jobtime}</td>
                      <td className="px-3 py-2">{j.total.toLocaleString()}</td>
                      <td className="px-3 py-2">{j.processed.toLocaleString()}</td>
                      <td className="px-3 py-2">{j.unprocessed.toLocaleString()}</td>
                      <td className="px-3 py-2">{j.pct}%</td>
                      <td className="px-3 py-2">{j.eta_hours != null ? `${j.eta_hours}h` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* C: Historical numbers + charts (shared range selector) */}
          <section className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Historical</h2>
              <select
                value={chartRange}
                onChange={(e) => setChartRange(e.target.value as ChartRange)}
                className="text-sm rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1.5"
              >
                {CHART_RANGE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {historical && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <KpiCard
                  icon={<TrendingUp className="h-4 w-4" />}
                  label="Total sent"
                  value={historical.total_sent.toLocaleString()}
                  sub={
                    chartRange === '30d' || chartRange === '90d'
                      ? 'Within event retention window'
                      : chartRangeLabel
                  }
                />
                <KpiCard
                  icon={<Activity className="h-4 w-4" />}
                  label="Avg rate"
                  value={formatRate(historical.avg_rate_per_hour)}
                  sub="Mean of 60m rates"
                />
                <KpiCard
                  icon={<TrendingUp className="h-4 w-4" />}
                  label="Peak rate"
                  value={formatRate(historical.peak_rate_per_hour)}
                  sub="Highest 60m window"
                />
                <KpiCard
                  icon={<AlertTriangle className="h-4 w-4" />}
                  label="Serious errors"
                  value={historical.total_serious_errors.toLocaleString()}
                  sub={chartRangeLabel}
                />
                <KpiCard
                  icon={<Activity className="h-4 w-4" />}
                  label="Avg queue"
                  value={historical.avg_queue.toLocaleString()}
                />
                <KpiCard
                  icon={<Activity className="h-4 w-4" />}
                  label="Peak queue"
                  value={historical.peak_queue.toLocaleString()}
                />
              </div>
            )}

            <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-4">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Charts</h3>
              {chartData.length === 0 ? (
                <p className="text-sm text-gray-400 py-8 text-center">
                  No snapshot data for this range — collector runs every minute. Longer ranges need snapshot retention ≥ lookback (default 90 days).
                </p>
              ) : (
                <div className="space-y-8">
                  <div>
                    <p className="text-xs text-gray-500 mb-2">
                      Send rate (actual emails/hour from last-60m window at each snapshot)
                    </p>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#37415133" />
                          <XAxis dataKey="time" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="rate" name="Rate/hr" stroke="#6366f1" dot={false} strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Postfix queue count</p>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#37415133" />
                          <XAxis dataKey="time" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Line type="monotone" dataKey="queue" name="Queue" stroke="#f59e0b" dot={false} strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Serious errors &amp; disk free (GB)</p>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#37415133" />
                          <XAxis dataKey="time" tick={{ fontSize: 11 }} />
                          <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Legend />
                          <Line yAxisId="left" type="monotone" dataKey="errors" name="Errors" stroke="#ef4444" dot={false} strokeWidth={2} />
                          <Line yAxisId="right" type="monotone" dataKey="disk" name="Disk GB" stroke="#22c55e" dot={false} strokeWidth={2} connectNulls />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* D: Log tables */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Delivery logs</h2>
            <select
              value={logsRange}
              onChange={(e) => setLogsRange(e.target.value as LogsRange)}
              className="text-sm rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1.5"
            >
              {LOGS_RANGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <section className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Successful Deliveries</h2>
              <button onClick={() => downloadCsv('events')} className="text-xs flex items-center gap-1 text-indigo-600 hover:underline">
                <Download className="h-3.5 w-3.5" /> CSV
              </button>
            </div>
            <div className="overflow-x-auto max-h-96">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 dark:bg-slate-700/60 sticky top-0">
                  <tr>
                    {['Time', 'Queue ID', 'Recipient', 'Domain', 'Relay', 'Delay', 'DSN', 'Status', 'Tracking ID'].map((h) => (
                      <th key={h} className="px-2 py-2 text-left font-semibold text-gray-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                  {events.length === 0 ? (
                    <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">No delivery events in range</td></tr>
                  ) : events.slice(0, 100).map((e) => (
                    <tr key={e.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/40">
                      <td className="px-2 py-1.5 whitespace-nowrap">{new Date(e.log_timestamp).toLocaleString()}</td>
                      <td className="px-2 py-1.5">{e.queue_id ?? '—'}</td>
                      <td className="px-2 py-1.5 max-w-[140px] truncate" title={e.recipient ?? ''}>{e.recipient ?? '—'}</td>
                      <td className="px-2 py-1.5">{e.recipient_domain ?? '—'}</td>
                      <td className="px-2 py-1.5 max-w-[120px] truncate" title={e.relay ?? ''}>{e.relay ?? '—'}</td>
                      <td className="px-2 py-1.5">{e.delay_seconds ?? '—'}</td>
                      <td className="px-2 py-1.5">{e.dsn ?? '—'}</td>
                      <td className="px-2 py-1.5">{e.status ?? '—'}</td>
                      <td className="px-2 py-1.5 max-w-[100px] truncate" title={e.tracking_id ?? ''}>{e.tracking_id ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-slate-700 flex-wrap gap-2">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Errors</h2>
              <div className="flex items-center gap-2">
                <select
                  value={errorFilter}
                  onChange={(e) => setErrorFilter(e.target.value)}
                  className="text-xs rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1"
                >
                  <option value="all">All errors</option>
                  <option value="serious">Serious only</option>
                  <option value="noise">Noise / ignored</option>
                  <option value="deferred">Deferred</option>
                  <option value="bounced">Bounced</option>
                  <option value="auth">Auth / SASL</option>
                  <option value="tls">TLS</option>
                  <option value="timeout">Timeout</option>
                  <option value="dns">DNS</option>
                  <option value="fatal">Fatal / Panic</option>
                </select>
                <button onClick={() => downloadCsv('errors')} className="text-xs flex items-center gap-1 text-indigo-600 hover:underline">
                  <Download className="h-3.5 w-3.5" /> CSV
                </button>
              </div>
            </div>
            <div className="overflow-x-auto max-h-96">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 dark:bg-slate-700/60 sticky top-0">
                  <tr>
                    {['Time', 'Severity', 'Category', 'Queue ID', 'Recipient', 'Message', 'Ignored'].map((h) => (
                      <th key={h} className="px-2 py-2 text-left font-semibold text-gray-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                  {errors.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No errors in range</td></tr>
                  ) : errors.slice(0, 100).map((e) => (
                    <tr key={e.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/40">
                      <td className="px-2 py-1.5 whitespace-nowrap">{new Date(e.log_timestamp).toLocaleString()}</td>
                      <td className="px-2 py-1.5">{e.severity}</td>
                      <td className="px-2 py-1.5">{e.category}</td>
                      <td className="px-2 py-1.5">{e.queue_id ?? '—'}</td>
                      <td className="px-2 py-1.5 max-w-[120px] truncate">{e.recipient ?? '—'}</td>
                      <td className="px-2 py-1.5 max-w-[300px] truncate" title={e.message ?? e.raw_line}>{e.message ?? '—'}</td>
                      <td className="px-2 py-1.5">{e.ignored_by_default ? 'Yes' : 'No'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Domain Breakdown</h2>
              <button onClick={() => downloadCsv('domains')} className="text-xs flex items-center gap-1 text-indigo-600 hover:underline">
                <Download className="h-3.5 w-3.5" /> CSV
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-slate-700/60">
                  <tr>
                    {['Domain', 'Sent', 'Deferred', 'Bounced', 'Expired', 'Avg Delay', 'Max Delay', '% Volume'].map((h) => (
                      <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                  {domains.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No domain data in range</td></tr>
                  ) : (showAllDomains ? domains : domains.slice(0, DOMAIN_PREVIEW_COUNT)).map((d) => (
                    <tr key={d.domain} className="hover:bg-gray-50 dark:hover:bg-slate-700/40">
                      <td className="px-3 py-2 font-medium">{d.domain}</td>
                      <td className="px-3 py-2">{d.sent_count.toLocaleString()}</td>
                      <td className="px-3 py-2">{d.deferred_count}</td>
                      <td className="px-3 py-2">{d.bounced_count}</td>
                      <td className="px-3 py-2">{d.expired_count}</td>
                      <td className="px-3 py-2">{d.avg_delay?.toFixed(2) ?? '—'}</td>
                      <td className="px-3 py-2">{d.max_delay?.toFixed(2) ?? '—'}</td>
                      <td className="px-3 py-2">{d.pct_of_volume}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {domains.length > DOMAIN_PREVIEW_COUNT && (
              <div className="px-4 py-2 border-t border-gray-100 dark:border-slate-700">
                <button
                  type="button"
                  onClick={() => setShowAllDomains((v) => !v)}
                  className="inline-flex items-center gap-1 text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  {showAllDomains ? (
                    <>
                      <ChevronUp className="h-4 w-4" />
                      Show first {DOMAIN_PREVIEW_COUNT}
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4" />
                      Show all {domains.length} domains
                    </>
                  )}
                </button>
              </div>
            )}
          </section>

          <section className="flex flex-wrap gap-2">
            {(['snapshots', 'events', 'errors', 'domains'] as const).map((t) => (
              <button
                key={t}
                onClick={() => downloadCsv(t)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700"
              >
                <Download className="h-4 w-4" />
                Export {t}
              </button>
            ))}
          </section>

          {isAdmin && settings && (
            <section className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Retention &amp; Thresholds</h2>
              <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">
                For Past 3 months charts, set snapshot retention to at least 90 days.
              </p>
              <div className="mb-5 p-3 rounded-md border border-indigo-200 dark:border-indigo-800/50 bg-indigo-50/50 dark:bg-indigo-900/10 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-800 dark:text-slate-200">
                    Target send rate (emails/hour)
                  </label>
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                    Presets map to Postfix conn + delay using a calibrated model
                    (SMTP overhead ≈ 1s). Observed: 1 conn / 1s ≈ 1,900/hr — not 3,600.
                    Estimate = conn × 3600 / (delay + 1) when delay &gt; 0; at 0s delay ≈ conn × 4,000/hr.
                    Or set Connections and Delay manually below, then Save.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {([
                    { id: 'none', label: 'No cap', target: 0 },
                    { id: 'recovery', label: '500/hr', target: 500 },
                    { id: '1k', label: '1,000/hr', target: 1000 },
                    { id: '12h', label: '1,200/hr', target: 1200 },
                    { id: '18h', label: '1,800/hr', target: 1800 },
                    { id: '36h', label: '3,600/hr', target: 3600 },
                    { id: 'warm', label: '7,200/hr', target: 7200 },
                    { id: 'cap', label: '18,000/hr', target: 18000 },
                  ] as const).map((p) => {
                    const active = targetRate === p.target;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => applyTargetPreset(p.target)}
                        className={`px-2.5 py-1 text-xs rounded-md border ${
                          active
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'border-gray-300 dark:border-slate-600 hover:bg-white dark:hover:bg-slate-700'
                        }`}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-xl">
                  <label className="block text-sm">
                    <span className="text-gray-600 dark:text-slate-400">Connections</span>
                    <input
                      type="number"
                      min={1}
                      max={20}
                      value={manualConc}
                      onChange={(e) => setManualPacing(Number(e.target.value), manualDelaySec)}
                      className="mt-1 w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="text-gray-600 dark:text-slate-400">Delay (seconds)</span>
                    <input
                      type="number"
                      min={0}
                      max={99}
                      value={manualDelaySec}
                      onChange={(e) => setManualPacing(manualConc, Number(e.target.value))}
                      className="mt-1 w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="text-gray-600 dark:text-slate-400">Emails / hour label</span>
                    <input
                      type="number"
                      min={0}
                      max={100000}
                      step={100}
                      value={targetRate}
                      onChange={(e) => {
                        settingsDirtyRef.current = true;
                        setSettings({
                          ...settings!,
                          target_hourly_rate: Number(e.target.value),
                        });
                      }}
                      className="mt-1 w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
                    />
                  </label>
                </div>
                <p className="text-sm text-gray-800 dark:text-slate-200">
                  {manualConc} conn / {manualDelaySec}s → est. ~{manualEst.toLocaleString()}/hr
                  {pacingPreview.concurrency !== manualConc || pacingPreview.delaySec !== manualDelaySec ? (
                    <span className="block text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                      Preset for {targetRate.toLocaleString()}/hr would be: {pacingPreview.summary}
                    </span>
                  ) : (
                    <span className="block text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                      {pacingPreview.summary}
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-500 dark:text-slate-400">
                  Live Postfix:{' '}
                  {settings.smtp_pacing_live
                    ? `${settings.smtp_pacing_live.concurrency} conn / ${settings.smtp_pacing_live.delayRaw} / ${settings.smtp_pacing_live.recipients} rcpt`
                    : settings.smtp_pacing_message || settings.smtp_rate_delay_live || 'could not read postconf'}
                  {settings.smtp_pacing_applied === false ? ' — not applied yet' : ''}
                </p>
                <details className="text-sm">
                  <summary className="cursor-pointer text-gray-600 dark:text-slate-400">
                    Advanced: recipients / connection
                  </summary>
                  <label className="block text-sm max-w-xs mt-2">
                    <span className="text-gray-600 dark:text-slate-400">Recipients / connection</span>
                    <input
                      type="number"
                      min={1}
                      max={1000}
                      value={settings.smtp_destination_recipient_limit ?? 50}
                      onChange={(e) => {
                        settingsDirtyRef.current = true;
                        setSettings({
                          ...settings,
                          smtp_destination_recipient_limit: Number(e.target.value),
                        });
                      }}
                      className="mt-1 w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
                    />
                  </label>
                </details>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {([
                  ['queue_warning_count', 'Queue warning count', 0, 1000000],
                  ['queue_critical_count', 'Queue critical count', 0, 1000000],
                  ['disk_warning_gb', 'Disk warning (GB)', 0, 10000],
                  ['disk_critical_gb', 'Disk critical (GB)', 0, 10000],
                  ['refresh_seconds', 'UI refresh (seconds)', 15, 300],
                  ['retention_snapshots_days', 'Snapshot retention (days)', 1, 365],
                  ['retention_events_days', 'Event retention (days)', 1, 90],
                  ['retention_errors_days', 'Error retention (days)', 1, 365],
                  ['serious_error_threshold', 'Error threshold for red (count/15m)', 0, 1000],
                ] as const).map(([key, label, min, max]) => (
                  <label key={key} className="block text-sm">
                    <span className="text-gray-600 dark:text-slate-400">{label}</span>
                    <input
                      type="number"
                      min={min}
                      max={max}
                      value={settings[key]}
                      onChange={(e) => {
                        settingsDirtyRef.current = true;
                        setSettings({ ...settings, [key]: Number(e.target.value) });
                      }}
                      className="mt-1 w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
                    />
                  </label>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-3">
                <button
                  onClick={saveSettings}
                  disabled={settingsSaving}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  {settingsSaving ? 'Saving…' : 'Save Settings'}
                </button>
                {settingsSaved && (
                  <span className="flex items-center gap-1 text-sm text-green-600">
                    <CheckCircle className="h-4 w-4" /> Saved
                  </span>
                )}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
